import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Users, GraduationCap, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const ImpactMapPage = () => {
  const { t, lang } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/impact-countries")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setCountries(list);
        if (list.length > 0) setActive(list[0]);
      })
      .catch(() => setCountries([]))
      .finally(() => setLoading(false));
    document.title = lang === "en" ? "Impact Map – Fondation Cœur Solidaire" : "Carte d'impact – Fondation Cœur Solidaire";
  }, [lang]);

  const totals = countries.reduce(
    (acc, c) => ({
      widows: acc.widows + (c.widows_supported || 0),
      orphans: acc.orphans + (c.orphans_supported || 0),
      programs: acc.programs + (c.programs || 0),
    }),
    { widows: 0, orphans: 0, programs: 0 }
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="impact-map-page">
      <Seo
        title={t.seo.impactMap.title}
        description={t.seo.impactMap.description}
        path={lang === "en" ? "/impact-map" : "/carte-impact"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors" data-testid="impact-back-home">
            <ArrowLeft size={14} /> {t.impactMapPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]">{t.impactMapPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="impact-map-title">
            {t.impactMapPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">{t.impactMapPage.intro}</p>

          <div className="grid grid-cols-3 gap-4 mt-10 max-w-2xl">
            <KpiTile value={countries.length} label={t.impactMapPage.kpis.countries} />
            <KpiTile value={totals.widows.toLocaleString(lang === "en" ? "en-US" : "fr-FR")} label={t.impactMapPage.kpis.widows} />
            <KpiTile value={totals.orphans.toLocaleString(lang === "en" ? "en-US" : "fr-FR")} label={t.impactMapPage.kpis.orphans} />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow">
          {loading ? (
            <div className="text-center text-[#64748B] py-12">{t.impactMapPage.loading}</div>
          ) : countries.length === 0 ? (
            <div className="text-center text-[#64748B] py-12">{t.impactMapPage.empty}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Country list (sidebar) */}
              <div className="lg:col-span-5 space-y-3" data-testid="impact-countries-list">
                {countries.map((c, i) => {
                  const isActive = active && active.id === c.id;
                  return (
                    <motion.button
                      key={c.id}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.04 }}
                      onClick={() => setActive(c)}
                      className={`w-full text-left p-5 rounded-xl border transition-all flex items-center gap-4 ${
                        isActive
                          ? "bg-[#002B49] text-white border-[#002B49] shadow-[0_8px_30px_-8px_rgba(0,43,73,0.4)]"
                          : "bg-white border-black/5 hover:border-[#C5A059]/40"
                      }`}
                      data-testid={`country-tile-${i}`}
                    >
                      <span className="text-3xl shrink-0" aria-hidden>{c.flag_emoji || "🌍"}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`font-serif text-lg font-semibold leading-tight ${isActive ? "text-white" : "text-[#002B49]"}`}>
                          {lang === "en" ? c.name_en : c.name_fr}
                        </div>
                        <div className={`text-xs mt-0.5 ${isActive ? "text-[#E8C77E]" : "text-[#64748B]"}`}>{c.region}</div>
                      </div>
                      <MapPin size={16} className={isActive ? "text-[#E8C77E]" : "text-[#C5A059]"} />
                    </motion.button>
                  );
                })}
              </div>

              {/* Detail panel */}
              <div className="lg:col-span-7 lg:sticky lg:top-24 self-start">
                {active && (
                  <motion.div
                    key={active.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_20px_60px_-30px_rgba(0,43,73,0.25)]"
                    data-testid="country-detail"
                  >
                    {active.image_url && (
                      <div className="aspect-[16/9] overflow-hidden bg-[#F3F0EA]">
                        <img src={active.image_url} alt={lang === "en" ? active.name_en : active.name_fr} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-6 md:p-8">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{active.flag_emoji || "🌍"}</span>
                        <div>
                          <h2 className="font-serif text-2xl md:text-3xl text-[#002B49] font-semibold leading-tight">
                            {lang === "en" ? active.name_en : active.name_fr}
                          </h2>
                          <div className="text-sm text-[#C5A059] mt-0.5">{active.region}</div>
                        </div>
                      </div>
                      <p className="text-[#334155] mt-5 leading-relaxed">
                        {lang === "en" ? active.summary_en : active.summary_fr}
                      </p>
                      <div className="grid grid-cols-3 gap-3 mt-7">
                        <Stat icon={Users} value={active.widows_supported.toLocaleString(lang === "en" ? "en-US" : "fr-FR")} label={t.impactMapPage.stats.widows} />
                        <Stat icon={GraduationCap} value={active.orphans_supported.toLocaleString(lang === "en" ? "en-US" : "fr-FR")} label={t.impactMapPage.stats.orphans} />
                        <Stat icon={Briefcase} value={active.programs} label={t.impactMapPage.stats.programs} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          )}

          <div className="mt-20 text-center bg-white rounded-2xl p-10 md:p-14 border border-black/5">
            <h2 className="font-serif text-2xl md:text-3xl text-[#002B49]">{t.impactMapPage.ctaTitle}</h2>
            <p className="text-[#64748B] mt-3 max-w-xl mx-auto">{t.impactMapPage.ctaSubtitle}</p>
            <Link to="/#donate" className="btn-primary inline-flex items-center mt-6" data-testid="impact-cta-donate">
              {t.impactMapPage.ctaButton}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const KpiTile = ({ value, label }) => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
    <div className="font-serif text-2xl md:text-3xl text-[#E8C77E] font-semibold">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-white/70 mt-1">{label}</div>
  </div>
);

const Stat = ({ icon: Icon, value, label }) => (
  <div className="bg-[#FDFBF7] border border-black/5 rounded-xl p-4 text-center">
    <Icon size={18} className="text-[#C5A059] mx-auto mb-2" />
    <div className="font-serif text-xl text-[#002B49] font-semibold">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-[#64748B] mt-1">{label}</div>
  </div>
);

export default ImpactMapPage;
